import React from 'react';
import { i18n } from 'config/locales';

import { FOREIGN } from 'config/formSections/foreign';
import { CITIZENSHIP, CITIZENSHIP_US_PASSPORT } from 'config/formSections/citizenship';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Field from 'components/Form/Field';
import Suggestions from 'components/Form/Suggestions';
import Name from 'components/Form/Name';
import DateControl from 'components/Form/DateControl';
import Text from 'components/Form/Text';
import { isFormSF852013, isPVQForm } from 'helpers/form';
import { CITIZENSHIP_STATUS } from 'constants/sections';
import Comments from 'components/Form/Comments';
import SvgComponent from 'components/Form/SvgComponent';
import Textarea from 'components/Form/Textarea';
import Passport from 'components/Form/PVQPassport';
import Accordion from 'components/Form/Accordion';
import { DateSummary, NameSummary, Summary } from 'components/Summary';
import { extractDate } from '../../History/dateranges';

const sectionConfig = {
  key: CITIZENSHIP_US_PASSPORT.key,
  section: CITIZENSHIP.name,
  store: FOREIGN.store,
  subsection: CITIZENSHIP_US_PASSPORT.name,
  storeKey: CITIZENSHIP_US_PASSPORT.storeKey,
};

export class UsPassport extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.formType = this.props.formType;
    this.section = section;
    this.subsection = subsection;
    // TODO: Temporary solution to limit risk of changing Redux, backend, and XML changes
    this.store = store;
    this.storeKey = storeKey;

    this.openSuggestion = this.openSuggestion.bind(this);

    this.number = React.createRef();
    this.state = {
      showSuggestionsModal: false,
    };

    this.handleNameError = this.handleNameError.bind(this);
    this.handleError = this.handleError.bind(this);
    this.openSuggestion = this.openSuggestion.bind(this);
    this.updateHasPassportBook = this.updateHasPassportBook.bind(this);
    this.updatePassportBooks = this.updatePassportBooks.bind(this);
    this.updateHasPassportCard = this.updateHasPassportCard.bind(this);
  }

  componentDidMount = () => {
    // set initial
    this.update({});
  };

  handleNameError(value, arr) {
    const arr2 = arr.map((err) => ({
      ...err,
      code:
        err.code.indexOf('required') !== -1
          ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
          : err.code,
    }));
    return this.props.onError(value, arr2);
  }

  update = (queue, fn) => {
    const { requireCitizenshipUsPassportName, requireCitizenshipUsPassportExpiration } = this.props;
    this.props.onUpdate(this.storeKey, {
      Name: this.props.Name,
      Number: this.props.Number,
      Card: this.props.Card,
      Issued: this.props.Issued,
      Expiration: this.props.Expiration,
      Comments: this.props.Comments,
      HasPassportBook: this.props.HasPassportBook,
      HasPassportBookExplanation: this.props.HasPassportBookExplanation,
      PassportBooks: this.props.PassportBooks,
      HasPassportCard: this.props.HasPassportCard,
      HasPassportCardExplanation: this.props.HasPassportCardExplanation,
      PassportCard: this.props.PassportCard,
      OptionalComments: this.props.OptionalComments,
      HasPassports: this.props.HasPassports,
      suggestedNames: this.props.suggestedNames,
      requireCitizenshipUsPassportExpiration,
      requireCitizenshipUsPassportName,
      ...queue,
    }, [CITIZENSHIP_STATUS]);

    if (fn) {
      fn();
    }
  };

  /**
   * Handle the change event.
   */
  updateCard = (values) => {
    this.update(
      {
        Card: values,
      },
      () => {
        // This allows us to force a blur/validation using
        // the new regular expression
        this.number.current.text.current.input.current.focus();
        this.number.current.text.input.current.blur();
      }
    );
  };

  /**
   * Handle when the yes/no option has been changed
   */
  updateBranch = (values) => {
    const isYes = values.value === 'Yes';
    this.update({
      HasPassports: values,
      Name: isYes ? this.props.Name : {},
      Number: isYes ? this.props.Number : '',
      Issued: isYes ? this.props.Issued : {},
      Expiration: isYes ? this.props.Expiration : {},
    });
  };

  updateName = (values) => {
    this.update({
      Name: values,
    });
  };

  updateNumber = (values) => {
    this.update({
      Number: values,
    });
  };

  updateIssued = (values) => {
    this.update({
      Issued: values,
    });
  };

  updateExpiration = (values) => {
    this.update({
      Expiration: values,
    });
  };

  updateComments = (values) => {
    this.update({
      Comments: values,
    });
  };

  updateOptionalComments = (values) => {
    this.update({
      OptionalComments: values,
    });
  };

  updateHasPassportBook = (values) => {
    this.update({
      HasPassportBook: values,
      HasPassportBookExplanation: (values.value === 'IDontKnow') ? this.props?.HasPassportBookExplanation : {}
    });
  };

  updateHasPassportBookExplanation = (values) => {
    this.update({
      HasPassportBookExplanation: values,
    });
  };

  updatePassportBooks = (values) => {
    this.update({
      PassportBooks: values,
    });
  };

  updateHasPassportCard = (values) => {
    this.update({
      HasPassportCard: values,
      HasPassportCardExplanation: (values.value === 'IDontKnow') ? this.props?.HasPassportCardExplanation : {}
    });
  };

  updateHasPassportCardExplanation = (values) => {
    this.update({
      HasPassportCardExplanation: values,
    });
  };

  updatePassportCard = (values) => {
    this.update({
      PassportCard: values,
    });
  };

  renderSuggestion = (suggestion = {}) => {
    const name = `${suggestion.first || ''} ${suggestion.middle || ''} ${suggestion.last || ''} ${suggestion.suffix || ''
    }`.trim();
    return <span>{name}</span>;
  };

  onSuggestion = (suggestion) => {
    this.update({
      Name: suggestion,
    });

    this.setState({ showSuggestionsModal: false });
  };

  onDismiss = () => {
    this.setState({ showSuggestionsModal: false });
  };

  openSuggestion() {
    this.setState({ showSuggestionsModal: true });
  }

  showSuggestions = () => {
    // If we have a name already, don't show
    if (this.props.Name && this.props.Name.first && this.props.Name.last) {
      return false;
    }

    // If we have suggestions, show them
    return !!this.props.suggestedNames.length;
  };

  passportBeforeCutoff = () => {
    if (this.props.Issued) {
      const cutoffDate = new Date('1/1/1990 00:00');
      const issueDate = extractDate(this.props.Issued);

      if (issueDate && issueDate < cutoffDate) {
        return true;
      }
    }

    return false;
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const name = NameSummary(o.Name);
    const dates = DateSummary(o.Issued);

    return Summary({
      type: i18n.t('citizenship.pvq.passportbook.collection.itemType'),
      index,
      left: name,
      right: dates,
    });
  };

  render() {
    const { showSuggestionsModal } = this.state;
    const cutOff = this.passportBeforeCutoff();
    const numberLength = cutOff ? '255' : '9';
    const numberRegEx = cutOff ? '^[a-zA-Z0-9]*$' : this.props.reBook;
    const {
      suggestedNames, formType, formVersion, errors
    } = this.props;
    const branchAttribute = 'HasPassports';
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div
        className="section-content passport"
        data-section={CITIZENSHIP.key}
        data-subsection={CITIZENSHIP_US_PASSPORT.key}
      >
        <h1 className="section-header">{i18n.t('citizenship.pvq.passportintro.title')}</h1>
        <h4 className="bottom-spacing">{i18n.t('citizenship.pvq.passportintro.info.text')}</h4>

        <Branch
          attribute="HasPassportBook"
          name="has_passport_book"
          label={i18n.t('citizenship.pvq.passportbook.question.title')}
          labelSize="h4"
          {...this.props.HasPassportBook}
          warning
          onUpdate={this.updateHasPassportBook}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          showDNK
        />
        {this.props.HasPassportBook.value === 'IDontKnow' && (
          <Field
            title={i18n.t('citizenship.pvq.passportbook.question.explanation')}
            titleSize="label"
            adjustFor="textarea"
            attribute="HasPassportBookExplanation"
          >
            <Textarea
              name="HasPassportBookExplanation"
              attribute="HasPassportBookExplanation"
              className="passport-explanation"
              {...this.props.HasPassportBookExplanation}
              onUpdate={this.updateHasPassportBookExplanation}
              onError={this.props.onError}
            />
          </Field>
        )}
        {this.props.HasPassportBook.value === 'Yes' && (
          <Accordion
            {...this.props.PassportBooks}
            onUpdate={this.updatePassportBooks}
            defaultState={this.props.defaultState}
            required={this.props.required}
            summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            scrollToBottom={this.props.scrollToBottom}
            scrollIntoView={this.props.scrollIntoView}
            description={i18n.t('citizenship.pvq.passportbook.collection.description')}
            appendTitle={i18n.t('citizenship.pvq.passportbook.collection.appendTitle')}
            appendLabel={i18n.t('citizenship.pvq.passportbook.collection.appendLabel')}
            attribute="PassportBooks"
          >
            <Passport
              name="Item"
              attribute="PassportBooks"
              onError={this.handleError}
              passportType="passportbook"
              bind
            />
          </Accordion>
        )}
        <Branch
          attribute="HasPassportCard"
          name="has_passport_card"
          label={i18n.t(`citizenship.pvq.passportcard.question.title`)}
          labelSize="h4"
          {...this.props.HasPassportCard}
          warning
          onUpdate={this.updateHasPassportCard}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          showDNK
        />
        {this.props.HasPassportCard.value === 'IDontKnow' && (
          <Field
            title={i18n.t(`citizenship.pvq.passportcard.question.explanation`)}
            titleSize="label"
            adjustFor="textarea"
            attribute="HasPassportCardExplanation"
          >
            <Textarea
              name="HasPassportCardExplanation"
              attribute="HasPassportCardExplanation"
              className="passport-explanation"
              {...this.props.HasPassportCardExplanation}
              onUpdate={this.updateHasPassportCardExplanation}
              onError={this.props.onError}
            />
          </Field>
        )}
        {this.props.HasPassportCard.value === 'Yes' && (
          <Field
            attribute="PassportCard"
          >
            <Passport
              name="passportcard"
              onError={this.handleError}
              passportType="passportcard"
              attribute="PassportCard"
              {...this.props.PassportCard}
              onUpdate={this.updatePassportCard}
            />
          </Field>
        )}
      </div>
    ) : (
      <div
        className="section-content passport"
        data-section={CITIZENSHIP.key}
        data-subsection={CITIZENSHIP_US_PASSPORT.key}
      >
        <h1 className="section-header">{i18n.t('citizenship.usPassport.title')}</h1>
        <h3>{i18n.t('citizenship.usPassport.info.text')}</h3>
        <Branch
          attribute={branchAttribute}
          name="has_passport"
          label={i18n.t('citizenship.usPassport.question.title')}
          labelSize="h4"
          {...this.props.HasPassports}
          warning
          onUpdate={this.updateBranch}
          onError={this.handleError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        />
        {!isFormSF852013(formType, formVersion) && (
          <Comments
            title={i18n.t('citizenship.comments.branch')}
            name={`${branchAttribute}OptionalComments`}
            className="branch-comment"
            attribute={`${branchAttribute}OptionalComments`}
            {...this.props.OptionalComments}
            onUpdate={this.updateOptionalComments}
            required={false}
          />
        )}
        <Show when={this.props.HasPassports.value === 'Yes'}>
          <div>
            {this.props.requireCitizenshipUsPassportName === true && (
              <div>
                <Field
                  title={i18n.t('citizenship.usPassport.name')}
                  titleSize="h4"
                  optional
                  className="no-margin-bottom"
                />
                {this.showSuggestions() && (
                  <div className="reuse-name">
                    <button
                      type="button"
                      className="reuse-name-open-modal"
                      title={i18n.t('citizenship.usPassport.names.reuse')}
                      onClick={this.openSuggestion} // eslint-disable-line @typescript-eslint/unbound-method
                    >
                      <div className="address-book" aria-hidden="true">
                        <SvgComponent className="address-book" src="/img/address-book.svg" />
                      </div>
                      <span>{i18n.t('citizenship.usPassport.names.reuse')}</span>
                    </button>
                  </div>
                )}
                <Suggestions
                  show={showSuggestionsModal}
                  suggestions={suggestedNames}
                  renderSuggestion={this.renderSuggestion}
                  withSuggestions
                  suggestionTitle={i18n.t('suggestions.name.title')}
                  suggestionParagraph={i18n.t('suggestions.name.para')}
                  suggestionLabel={i18n.t('suggestions.name.label')}
                  suggestionDismissLabel={i18n.t('suggestions.name.dismiss')}
                  suggestionUseLabel={i18n.t('suggestions.name.use')}
                  onSuggestion={this.onSuggestion}
                  onDismiss={this.onDismiss}
                />
                <Field
                  optional
                  title={i18n.t('identification.name.title')}
                  filterErrors={Name.requiredErrorsOnly}
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <Name
                    attribute="Name"
                    aria-label="Provide your full name"
                    name="name"
                    {...this.props.Name}
                    onUpdate={this.updateName}
                    onError={this.handleNameError} // eslint-disable-line @typescript-eslint/unbound-method
                    required={this.props.required}
                    scrollIntoView={this.props.scrollIntoView}
                  />
                </Field>
              </div>
            )}
            <Field
              attribute="Issued"
              title={i18n.t('citizenship.usPassport.issued')}
              help={i18n.t('citizenship.usPassport.help.issued.message')}
              helpTitle={i18n.t('citizenship.usPassport.help.issued.title')}
              helpMessage={i18n.t('citizenship.usPassport.help.issued.message')}
              adjustFor="labels"
              shrink
              scrollIntoView={this.props.scrollIntoView}
            >
              <DateControl
                attribute="Issued"
                name="issued"
                className="passport-issued"
                {...this.props.Issued}
                minDateEqualTo
                onUpdate={this.updateIssued}
                onError={this.handleError}
                required={this.props.required}
              />
            </Field>

            {this.props.requireCitizenshipUsPassportExpiration === true && (
              <Field
                attribute="Expiration"
                title={i18n.t('citizenship.usPassport.expiration')}
                help={i18n.t('citizenship.usPassport.help.expiration.message')}
                helpTitle={i18n.t('citizenship.usPassport.help.expiration.title')}
                helpMessage={i18n.t('citizenship.usPassport.help.expiration.message')}
                adjustFor="labels"
                shrink
                scrollIntoView={this.props.scrollIntoView}
              >
                <DateControl
                  attribute="Expiration"
                  name="expiration"
                  className="passport-expiration"
                  {...this.props.Expiration}
                  minDate={this.props.Issued}
                  minDateEqualTo
                  prefix="passportInformation"
                  onUpdate={this.updateExpiration}
                  onError={this.handleError}
                  required={this.props.required}
                />
              </Field>
            )}

            <Field
              attribute="Number"
              title={i18n.t('citizenship.usPassport.label.bookNumber')}
              help={i18n.t('citizenship.usPassport.help.number.message')}
              helpTitle={i18n.t('citizenship.usPassport.help.number.title')}
              helpMessage={i18n.t('citizenship.usPassport.help.number.message')}
              errorPrefix="passport"
              adjustFor="buttons"
              shrink
              scrollIntoView={this.props.scrollIntoView}
            >
              <div>
                <Text
                  attribute="Number"
                  name="number"
                  {...this.props.Number}
                  pattern={numberRegEx}
                  maxlength={numberLength}
                  className="number passport-number"
                  ref={this.number}
                  prefix="passport"
                  onUpdate={this.updateNumber}
                  onError={this.handleError}
                  required={this.props.required}
                />
              </div>
            </Field>
            {!isFormSF852013(formType, formVersion) && (
              <Comments
                title={i18n.t('citizenship.comments.branch')}
                name={`${branchAttribute}Comments`}
                className="branch-comment"
                attribute={`${branchAttribute}Comments`}
                {...this.props.Comments}
                onUpdate={this.updateComments}
                required={false}
              />
            )}
          </div>
        </Show>
      </div>
    );
  }
}

// Regular expressions were based on the information provided by
// U.S. Citizenship and Immigration Services (USCIS) at:
//
// https://e-verify-uscis.gov/esp/help/EvHelpPassportandPassportCardNbr.htm
UsPassport.defaultProps = {
  Name: {},
  Number: {},
  Card: { value: 'Book' },
  Issued: {},
  Expiration: {},
  Comments: {},
  OptionalComments: {},
  HasPassports: {},
  HasPassportBook: {},
  HasPassportBookExplanation: {},
  PassportBooks: {},
  HasPassportCard: {},
  HasPassportCardExplanation: {},
  PassportCard: {},
  defaultState: true,
  applicantName: {},
  suggestedNames: [],
  reBook: '^[a-zA-Z0-9]{9}$',
  onUpdate: () => { },
  onError: (value, arr) => arr,
  section: 'foreign',
  subsection: 'passport',
  dispatch: () => { },
  errors: [],
};

export default connectSubsection(UsPassport, sectionConfig);
